import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAuth } from '../../contexts/AuthContext';
import salesService from '../../services/salesService';
import {
  getTerminalTargetRoute,
  setTerminalContext,
  type TerminalContext,
  type TerminalDeviceMode,
} from '../../services/auth/terminalSession';

/**
 * `/cihaz-oturum/:code` — satış terminali kodunu oturuma çeviren guard'sız
 * bootstrap rotası.
 *
 * local-service, kayıtlı bir kod için bu adresi açar. Akış: koddan gerçek bir
 * own-auth oturumu al → terminal bağlamını yaz → cihaz moduna karşılık gelen
 * satış ekranına `replace` ile git.
 *
 * Hata durumunda login ekranına YÖNLENDİRİLMEZ: terminal kullanıcısı login
 * ekranını hiç görmemeli, gördüğünde de yapabileceği bir şey yok (elinde
 * e-posta/şifre değil kod var). Bunun yerine tekrar denenebilir bir hata
 * ekranı gösterilir.
 */
const VALID_MODES: TerminalDeviceMode[] = [
  'sales_quicksale',
  'sales_restaurant',
  'sales_package',
  'sales_pyk',
];

/**
 * Paket modunda cihaz kaydı PackageScreens.Id tutuyor, `/paket-ekrani/:screenId`
 * rotası ise QuickSaleScreens.Id bekliyor: PackageOrderScreen hızlı satış ekran
 * listesinde `Id === screenId && ScreenType === 'Package'` araması yapıyor. İki
 * tablonun Id dizileri bağımsız ilerlediği için doğru değere ancak paket ekranı
 * kaydını okuyarak ulaşılabilir; tahminle denemek başka bir şubenin ekranını
 * açabilirdi.
 */
async function resolvePackageRouteScreenId(packageScreenId: number): Promise<number> {
  const packageScreen = await salesService
    .getPackageScreenById(packageScreenId)
    .catch((e: unknown) => {
      console.error('[TerminalBootstrap] Paket ekranı okunamadı:', e);
      return null;
    });

  const quickSaleScreenId = packageScreen?.QuickSaleScreenId;
  if (!quickSaleScreenId) {
    throw new Error(
      'Bu koda bağlı paket sipariş ekranı bulunamadı. Ekran silinmiş olabilir; ' +
        'satış ekranı ayarlarındaki "Login İşlemleri" sekmesinden kodu yeniden üretin.',
    );
  }

  return quickSaleScreenId;
}

const TerminalSessionBootstrap: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { loginTerminal, loading: authLoading } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  // React 18 StrictMode geliştirmede effect'leri iki kez çalıştırıyor; kodu iki
  // kez göndermek gereksiz bir ikinci oturum + refresh token satırı üretir.
  const startedForAttempt = useRef<number | null>(null);

  // Bileşen GERÇEKTEN söküldü mü?
  //
  // Effect cleanup'i yalnızca unmount'ta değil, her bağımlılık değişiminde de
  // çalışıyor. Yerel bir `cancelled` bayrağı bu ikisini ayırt edemediği için
  // aşağıdaki zincir oturumu sessizce öldürüyordu: loginTerminal içindeki
  // setCurrentUser + fetchUserDetails AuthProvider'ı yeniden render ediyor, bu
  // da yeni bir `loginTerminal` kimliği üretiyor, bağımlılık değiştiği için
  // cleanup `cancelled = true` yazıyor ve effect yeniden kuruluyor — ama
  // `startedForAttempt` guard'ı yüzünden hiçbir şey yapmıyor. Sonuçta kod
  // doğrulanıp oturum açıldığı halde navigate hiç çalışmıyor ve ekran
  // "hazırlanıyor" spinner'ında kalıyordu.
  const mountedRef = useRef(true);

  // Aynı sebeple `loginTerminal` ve `navigate` bağımlılık listesinde DURMAMALI;
  // güncel değerleri ref üzerinden okunuyor.
  const loginTerminalRef = useRef(loginTerminal);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    loginTerminalRef.current = loginTerminal;
    navigateRef.current = navigate;
  });

  useEffect(() => {
    mountedRef.current = true;
    console.log('[TerminalBootstrap] MOUNT', { code, url: window.location.href });
    return () => {
      mountedRef.current = false;
      console.log('[TerminalBootstrap] UNMOUNT', { url: window.location.href });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('[TerminalBootstrap] effect', {
      code,
      authLoading,
      attempt,
      startedForAttempt: startedForAttempt.current,
    });

    if (!code) {
      setError('Terminal kodu bulunamadı.');
      return;
    }
    // AuthContext'in oturum hydrate'i bitene kadar BEKLE.
    //
    // Aksi halde şu yarış oluşuyor: makinede eski/iptal edilmiş bir terminal
    // oturumu kaldıysa hydrate paralelde token yenilemeye çalışır, sunucu 401
    // döner ve ownAuthSession `clearSession()` çağırır — bu da loginTerminal'in
    // yeni yazdığı token'ları siler ve `refresh-failed` ile currentUser'ı
    // null'lar. Sonuç: kod doğru olduğu halde terminal boş ekranda kalır
    // (restoran/paket modlarında PrivateRoute login'e atar).
    if (authLoading) {
      console.log('[TerminalBootstrap] authLoading=true, bekleniyor');
      return;
    }
    if (startedForAttempt.current === attempt) {
      console.log('[TerminalBootstrap] bu deneme zaten baslatildi, atlaniyor', { attempt });
      return;
    }
    startedForAttempt.current = attempt;

    (async () => {
      try {
        console.log('[TerminalBootstrap] loginTerminal cagriliyor', { code });
        const { terminal } = await loginTerminalRef.current(code);
        console.log('[TerminalBootstrap] terminal bilgisi alindi', terminal);

        if (!VALID_MODES.includes(terminal.deviceMode as TerminalDeviceMode)) {
          throw new Error(`Bu cihaz için tanımlı ekran tipi tanınmıyor: ${terminal.deviceMode}`);
        }

        const deviceMode = terminal.deviceMode as TerminalDeviceMode;
        const routeScreenId =
          deviceMode === 'sales_package'
            ? await resolvePackageRouteScreenId(terminal.linkedScreenId)
            : undefined;

        const context: TerminalContext = {
          screenCode: terminal.screenCode,
          deviceMode,
          linkedScreenId: terminal.linkedScreenId,
          routeScreenId,
          screenName: terminal.screenName,
          branchId: terminal.branchId ?? null,
        };

        // Bağlam yönlendirmeden ÖNCE yazılmalı: hedef ekranın erişim kontrolü
        // (useQuickSaleData) ilk render'da bunu okuyor.
        setTerminalContext(context);
        console.log('[TerminalBootstrap] baglam yazildi', context);

        if (!mountedRef.current) {
          console.warn('[TerminalBootstrap] bilesen sokulmus, yonlendirme atlandi');
          return;
        }
        const target = getTerminalTargetRoute(context);
        console.log('[TerminalBootstrap] yonlendiriliyor', { target });
        navigateRef.current(target, { replace: true });
      } catch (e) {
        if (!mountedRef.current) {
          console.warn('[TerminalBootstrap] bilesen sokulmus, hata yutuldu', e);
          return;
        }
        const message =
          e instanceof Error ? e.message : 'Terminal oturumu açılamadı.';
        console.error('[TerminalSessionBootstrap] Oturum açılamadı:', e);
        setError(message);
      }
    })();
  }, [code, attempt, authLoading]);

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }} elevation={3}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 56, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Satış ekranı açılamadı
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {error}
          </Typography>
          {code && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
              Terminal kodu: {code}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={() => {
              setError(null);
              setAttempt((n) => n + 1);
            }}
          >
            Tekrar dene
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        bgcolor: 'grey.100',
      }}
    >
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        Satış ekranı hazırlanıyor…
      </Typography>
    </Box>
  );
};

export default TerminalSessionBootstrap;
